import { useAuth } from "../../../../features/auth/hooks";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useUserName } from "@shared/selectors/globalSelectors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import { 
  Bell, 
  ChevronDown, 
  LogOut, 
  User, 
  Settings, 
  HelpCircle, 
  ArrowRight 
} from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useNotifications } from "../../../notification/NotificationContext";
import {
  getNotificationTitle,
  getNotificationDescription,
  isNotificationUnread,
  getNotificationSeverity,
} from "../../../notification/utils";

interface TopnavProps {}

export const Topnav: React.FC<TopnavProps> = () => {
  const { firstName, lastName, remoteConsumerNumber } = getLoginDataFromStorage();
  const name = `${firstName || ''} ${lastName || ''}`.trim() || "Guest User";
  const { logout } = useAuth();
  const { t } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Live notification data from WebSocket
  const { notifications, unreadCount } = useNotifications();

  // Show at most 4 notifications in the dropdown preview
  const previewNotifications = notifications.slice(0, 4);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (fullName: string): string => {
    if (!fullName) return "";
    const names = fullName.trim().split(" ");
    return names.slice(0, 2).map((n) => n.charAt(0).toUpperCase()).join("");
  };

  const middleInitial = lastName ? lastName.charAt(0) + "." : "";
  const displayShortName = `${firstName || ''} ${middleInitial}`.trim() || 'Guest';

  return (
    <header className="w-full bg-white shadow-sm flex items-center justify-between px-6 h-16 fixed top-0 left-0 z-50">
      <span className="text-xl font-bold flex items-center gap-1">
        <span className="text-black">SMART</span>
        <span className="text-blue-600">360</span>
      </span>

      <div className="flex items-center gap-6">
        
        {/* Notifications Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="focus:outline-none relative mt-1">
            <Bell className="h-6 w-6 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border-2 border-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[380px] p-0 rounded-2xl shadow-xl overflow-hidden mt-2">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">{t("Notifications")}</h3>
              <Link to="/notification" className="text-blue-600 text-sm font-medium hover:underline">
                {t("View all")}
              </Link>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {previewNotifications.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  {t("No notifications")}
                </div>
              ) : (
                previewNotifications.map((notif) => {
                  const isUnread = isNotificationUnread(notif);
                  const severity = getNotificationSeverity(notif);
                  const title = getNotificationTitle(notif);
                  const description = getNotificationDescription(notif);

                  return (
                    <div key={notif.id} className="p-4 border-b border-gray-100 hover:bg-gray-50 flex gap-3 cursor-pointer">
                      <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                        isUnread
                          ? (severity === 'error' || severity === 'critical' ? 'bg-red-500' : 'bg-blue-500')
                          : 'bg-gray-300'
                      }`}></div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{title}</h4>
                        <p className="text-xs text-gray-500 leading-snug">
                          {description}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
              <Link to="/notification" className="text-blue-600 text-sm font-medium hover:underline flex items-center justify-center">
                {t("See all {{count}} notifications", { count: notifications.length })} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="focus:outline-none flex items-center gap-3 rounded-[28px] p-1.5 pr-4 hover:bg-gray-50 transition-colors bg-white">
              <div className="w-9 h-9 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold tracking-wide text-sm shadow-sm">
                {getInitials(name)}
              </div>
              <div className="flex flex-col items-start text-left mr-2">
                <span className="text-sm font-bold text-gray-900 leading-tight">
                  {displayShortName}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  {remoteConsumerNumber || 'N/A'}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 rounded-2xl p-2 shadow-xl mt-2">
            <div className="px-3 py-3 border-b border-gray-100 mb-2">
              <p className="font-bold text-gray-900 text-base">{name}</p>
              <p className="text-sm text-gray-500 font-medium">{remoteConsumerNumber || 'N/A'}</p>
            </div>
            
            <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer hover:bg-gray-50">
              <Link to="/profile" className="flex items-center gap-3 text-gray-700">
                <User className="w-5 h-5 text-gray-400" />
                <span className="font-medium text-[15px]">{t("My Profile")}</span>
              </Link>
            </DropdownMenuItem>
            
            <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer hover:bg-gray-50">
              <Link to="/support-center?tab=offices" className="flex items-center gap-3 text-gray-700">
                <HelpCircle className="w-5 h-5 text-gray-400" />
                <span className="font-medium text-[15px]">{t("Support")}</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="my-2 bg-gray-100" />

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-red-50 text-red-600 transition-colors">
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium text-[15px]">{t("Sign Out")}</span>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("Are you sure you want to logout?")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("This will end your current session and you'll need to login again to access your account.")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? t("Logging out...") : t("Logout")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
